'use client';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useMemo, useRef, useState } from 'react';

import { routes } from '~constants/routes';
import type { RegisterAnalytics } from '~features/annals/types';
import { useModal } from '~hooks/useModal';
import { usePathname, useRouter } from '~i18n/navigation';
const ForgotPasswordModal = dynamic(
	() => import('~features/auth').then((mod) => mod.ForgotPasswordModal),
	{
		ssr: false,
	}
);
const LoginModal = dynamic(
	() => import('~features/auth').then((mod) => mod.LoginModal),
	{
		ssr: false,
	}
);
const RegisterModal = dynamic(
	() => import('~features/auth').then((mod) => mod.RegisterModal),
	{
		ssr: false,
	}
);
import dynamic from 'next/dynamic';

import { AuthContext } from './index';
import type {
	AuthSuccessCallback,
	AuthSuccessCallbackType,
	RegLoginOpenCfg,
	TAuthActions,
} from './types';

type Props = {
	children: React.ReactNode;
};

const addRegistrationTrackingScript = () => {
	const registrationDataGoal = '389d29885335ad2076752e39f70423d0';
	if (document.querySelector(`script[data-goal="${registrationDataGoal}"]`)) {
		return;
	}

	const script = document.createElement('script');
	script.type = 'application/javascript';
	script.src = 'https://a.magsrv.com/tag_gen.js';
	script.setAttribute('data-goal', registrationDataGoal);

	document.head.appendChild(script);
};

export const AuthContextProvider = ({ children }: Props) => {
	const { refresh } = useRouter();
	const pathname = usePathname();

	const analyticsRef = useRef<RegisterAnalytics>({});
	const [authSuccessCallback, setAuthSuccessCallback] =
		useState<null | AuthSuccessCallback>(null);

	const {
		isOpen: isRegisterOpen,
		open: openRegisterModal,
		close: closeRegisterModal,
	} = useModal();
	const {
		isOpen: isLoginOpen,
		open: openLoginModal,
		close: closeLoginModal,
	} = useModal();
	const {
		isOpen: isResetOpen,
		open: openResetModal,
		close: closeResetModal,
	} = useModal();

	const switchToLogin = useCallback(() => {
		closeRegisterModal();
		closeResetModal();
		openLoginModal()();
	}, []); // eslint-disable-line
	const switchToRegister = useCallback(() => {
		closeLoginModal();
		openRegisterModal()();
	}, []); // eslint-disable-line
	const switchToReset = useCallback(() => {
		closeLoginModal();
		openResetModal()();
	}, []); // eslint-disable-line

	const handleCloseRegister = useCallback(() => {
		closeRegisterModal();
		setAuthSuccessCallback(null);
	}, [closeRegisterModal]);

	const handleCloseLogin = useCallback(() => {
		closeLoginModal();
		setAuthSuccessCallback(null);
	}, [closeLoginModal]);

	const handleSuccess = (type: AuthSuccessCallbackType) => {
		if (
			pathname === routes.index ||
			pathname.startsWith(routes.explore.models) ||
			pathname.startsWith(routes.gallery.home)
		) {
			refresh();
		}

		if (type === 'login') {
			closeLoginModal();
		} else if (type == 'register') {
			addRegistrationTrackingScript();
			closeRegisterModal();
		}

		if (authSuccessCallback) {
			authSuccessCallback();
			setAuthSuccessCallback(null);
		}
	};

	const openRegister = useCallback((cfg?: RegLoginOpenCfg) => {
		setAuthSuccessCallback(() => cfg?.cb ?? null);
		analyticsRef.current = cfg?.analytics || {};
		openRegisterModal()();
	}, []); // eslint-disable-line
	const openLogin = useCallback((cfg?: RegLoginOpenCfg) => {
		setAuthSuccessCallback(() => cfg?.cb ?? null);
		analyticsRef.current = cfg?.analytics || {};
		openLoginModal()();
		}, []); // eslint-disable-line

	const value: TAuthActions = useMemo(
		() => ({ openLogin, openRegister }),
		[openLogin, openRegister]
	);

	return (
		<AuthContext.Provider value={value}>
			{children}

			<AnimatePresence mode="wait" initial={false}>
				{isRegisterOpen && (
					<RegisterModal
						analytics={analyticsRef.current}
						open={true}
						locked={true}
						onClose={handleCloseRegister}
						onLoginClick={switchToLogin}
						onSuccess={handleSuccess}
					/>
				)}

				{isLoginOpen && (
					<LoginModal
						analytics={analyticsRef.current}
						open={true}
						locked={true}
						onClose={handleCloseLogin}
						onRegisterClick={switchToRegister}
						onResetPassClick={switchToReset}
						onSuccess={handleSuccess}
					/>
				)}

				{isResetOpen && (
					<ForgotPasswordModal
						open={true}
						locked={true}
						onClose={closeResetModal}
						onBack={switchToLogin}
					/>
				)}
			</AnimatePresence>
		</AuthContext.Provider>
	);
};
